import type { FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { requestPasswordReset } from '../lib/api';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useEmailField } from '../hooks/useEmailValidation';
import { connectionErrorMessage, getErrorStatus } from './authErrors';

const FORGOT_EMAIL_ERROR_ID = 'forgot-email-error';

export function ForgotPasswordForm() {
  useDocumentTitle('Forgot password');
  const emailField = useEmailField('');
  const email = emailField.value;
  const setEmail = emailField.setValue;

  const mutation = useMutation({
    mutationFn: requestPasswordReset,
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    emailField.onBlur();
    if (!email.trim() || !emailField.isValid) return;
    mutation.mutate(email.trim());
  }

  const error = mutation.error;
  const emailError =
    emailField.error ??
    (emailField.touched && email.trim().length === 0
      ? 'Enter your email address.'
      : null);

  if (mutation.isSuccess) {
    return (
      <div className="login-form" role="status" aria-live="polite">
        <h2>Check your email</h2>
        <p className="login-form-subtitle">
          If an account exists for <strong>{email}</strong>, we've sent a
          password-reset link. Click it to choose a new password.
        </p>
        <p className="login-form-cli-hint">
          Didn't get it? Check spam. The link expires in a few hours; you can
          request another from this page.
        </p>
        <a href="/" className="login-form-submit">
          Back to Sign in
        </a>
      </div>
    );
  }

  return (
    <form
      className="login-form"
      onSubmit={handleSubmit}
      aria-label="Forgot password"
      noValidate
    >
      <h2>Forgot password</h2>
      <p className="login-form-subtitle">
        Enter the email associated with your account. We'll send you a link to
        choose a new password.
      </p>

      {error && (
        <div className="login-form-error" role="alert" aria-live="assertive">
          {forgotPasswordErrorMessage(error)}
        </div>
      )}

      <label className="login-form-field">
        <span>Email</span>
        <input
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (mutation.isError) mutation.reset();
          }}
          onBlur={emailField.onBlur}
          disabled={mutation.isPending}
          aria-invalid={emailError ? true : undefined}
          aria-describedby={emailError ? FORGOT_EMAIL_ERROR_ID : undefined}
        />
        {emailError && (
          <span
            id={FORGOT_EMAIL_ERROR_ID}
            className="login-form-field-error"
            aria-live="polite"
          >
            {emailError}
          </span>
        )}
      </label>

      <button
        type="submit"
        className="login-form-submit"
        disabled={mutation.isPending || !email.trim() || !emailField.isValid}
      >
        {mutation.isPending && (
          <span className="button-spinner" aria-hidden="true" />
        )}
        {mutation.isPending ? 'Sending…' : 'Send reset link'}
      </button>

      <div className="login-form-links">
        <a href="/">Back to Sign in</a>
      </div>
    </form>
  );
}

function forgotPasswordErrorMessage(error: unknown): string {
  const status = getErrorStatus(error);
  if (status === 400 || status === 422) {
    return 'Enter a valid email address and try again.';
  }
  if (status === 429) {
    return 'Too many password reset requests. Wait a few minutes, then try again.';
  }
  if (status === null) return connectionErrorMessage();
  if (status >= 500) {
    return "We couldn't send the reset email because the server had a problem. Try again in a minute.";
  }
  return "We couldn't send the reset email. Try again in a minute.";
}
